import type { Edge } from "reactflow";

import { JOURNEY_NODES_WITH_BRANCH_NODES } from "src/pages/journeys/constants";
import {
  type JourneyNode,
  isExecutableJourneyNode,
} from "src/pages/journeys/types";
import { JourneyNodeType } from "src/types/journeys";
import {
  isSegmentBranchNode,
  isWaitUntilBranchNode,
  isSplitBranchNode,
  isEntryAudienceExperimentBranchNode,
} from "./type-guards";
import { getOutgoersSafe } from ".";

/**
 * Get all branch nodes of a given node
 */
export const getBranchNodes = (
  nodeId: string,
  nodes: JourneyNode[],
  edges: Edge[],
) => {
  const parentNode = nodes.find((node) => node.id === nodeId);

  if (parentNode) {
    const outgoingNodes = getOutgoersSafe(parentNode, nodes, edges);

    if (
      parentNode.type === JourneyNodeType.Segments ||
      parentNode.type === JourneyNodeType.Function
    ) {
      return outgoingNodes.filter(isSegmentBranchNode);
    }

    if (parentNode.type === JourneyNodeType.WaitUntilEvent) {
      return outgoingNodes.filter(isWaitUntilBranchNode);
    }

    if (parentNode.type === JourneyNodeType.Splits) {
      return outgoingNodes.filter(isSplitBranchNode);
    }

    if (parentNode.type === JourneyNodeType.EntryAudienceExperiment) {
      return outgoingNodes.filter(isEntryAudienceExperimentBranchNode);
    }
  }

  return [];
};

// Checked on the node rather than its type because a Function node has branches only once it has a success
// condition, while the other branch parents always do.
export const hasBranchNodes = (
  node: JourneyNode,
  nodes: JourneyNode[],
  edges: Edge[],
): boolean =>
  (isExecutableJourneyNode(node) &&
    JOURNEY_NODES_WITH_BRANCH_NODES.includes(node.data.config.type)) ||
  getBranchNodes(node.id, nodes, edges).length > 0;
